import type { Option } from 'shared/ui/MultiSelect/interfaces';
import { useUrlResolver } from 'shared/lib/url';
import { isNumber, isObject, isString } from 'shared/lib/type-guards';
import { useHistory } from 'react-router-dom';
import { PAGE_PARAM_NAME } from 'shared/ui/Pagination/constants';
import type { UseInfiniteQueryResult } from '@tanstack/react-query';
import type { CollectionSuccessApiResponse } from 'shared/lib/api';

type FilterArray = Partial<Record<'from' | 'to', number>>[];

export const parseFromToFilters = (allParams: URLSearchParams, filterName: string) => {
  const filters: FilterArray = [];

  allParams.forEach((value, key) => {
    if (key.startsWith(filterName)) {
      const regex = /\[(\d+)]\[(from|to)]$/;

      const match = regex.exec(key);

      if (!match) {
        return;
      }

      const index = Number(match[1]);
      const type = match[2] as 'from' | 'to';

      filters[index] = filters[index] || {};
      filters[index][type] = parseInt(value, 10);
    }
  });

  return filters;
};

export const mapFiltersFromUrlToOptions = (allOptions: Option[], filters: FilterArray) => {
  const matchedOptions = allOptions.filter((option) => {
    return filters.some((filter) => {
      const matchesFrom = filter.from === option.from;
      const matchesTo = option.to ? filter.to === option.to : true;

      return matchesFrom && matchesTo;
    });
  });

  return matchedOptions;
};

export const getOptionKey = (id: string, text: string) => `${id}_${text}`;

export function getMenuBaseProps<T extends Option>({
  selectedOptions,
  option,
}: {
  selectedOptions: T[];
  option: Option;
}) {
  const key = getOptionKey(option.id, option.text);

  return {
    selected: selectedOptions.some((selectedOption) => getOptionKey(selectedOption.id, selectedOption.text) === key),
    filteredSelectedOptions: selectedOptions.filter(
      (selectedOption) => getOptionKey(selectedOption.id, selectedOption.text) !== key
    ),
    key,
  };
}

export const isOptionsWithFilterKey = (options: Option[]): options is Required<Option>[] => {
  return isObject(options) && !!options.length && 'filterKey' in options[0] && isString(options[0].filterKey);
};

export const isOptionsWithFromTo = (options: Option[]): options is Required<Option>[] => {
  return isObject(options) && !!options.length && (isNumber(options[0].from) || isNumber(options[0].to));
};

export const useUrlParamCommands = () => {
  const { getUrl, updateHistory, addParamToUrl, removeParamFromUrl } = useUrlResolver();
  const history = useHistory();

  const paginationReset = (url: URL) => {
    // pagination reset
    const hasPage = url.searchParams.has(PAGE_PARAM_NAME);

    if (hasPage) {
      url.searchParams.delete(PAGE_PARAM_NAME);
      url.searchParams.append(PAGE_PARAM_NAME, '1');
    }

    return url;
  };

  const updateURLParamCommand = (selectedItems: Option[], prevSelectedOptions: Option[], filterName: string) => {
    const url = getUrl();

    // adding items
    selectedItems.forEach((selectedItem) => {
      // we need to update ts version to avoid these errors
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const hasItem = url.searchParams.has(filterName, selectedItem.id);

      if (!hasItem) {
        url.searchParams.append(filterName, selectedItem.id);
      }
    });

    // deleting items
    prevSelectedOptions.forEach((prevSelectedItem) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const hasItem = url.searchParams.has(filterName, prevSelectedItem.id);
      const shouldKeep = selectedItems.find((selectedItem) => selectedItem.id === prevSelectedItem.id);

      if (hasItem && !shouldKeep) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        url.searchParams.delete(filterName, prevSelectedItem.id);
      }
    });

    paginationReset(url);
    history.push({ search: url.searchParams.toString() });
  };

  const updateFilterKeyURLParamCommand = (propsOptions: Option[], selectedItems: Option[]) => {
    const url = getUrl();

    if (!isOptionsWithFilterKey(propsOptions)) {
      throw new Error(`Options don't have a filter key`);
    }
    propsOptions.forEach((option) => {
      removeParamFromUrl({ name: option.filterKey, url });
    });
    selectedItems.forEach((item) => {
      if (item.filterKey) {
        addParamToUrl({ name: item.filterKey, value: item.id, url });
      }
    });
    paginationReset(url);
    updateHistory({ url });
  };

  const updateFromToURLParamCommand = (selectedItems: Option[], prevSelectedOptions: Option[], filterName: string) => {
    const url = getUrl();

    // adding items
    selectedItems.forEach((selectedItem) => {
      const fromKey = `${filterName}[${selectedItem.id}][from]`;
      const toKey = `${filterName}[${selectedItem.id}][to]`;
      const hasItemFrom = url.searchParams.has(fromKey, `${selectedItem.from}`);

      if (!hasItemFrom && selectedItem.from) {
        url.searchParams.append(fromKey, selectedItem.from.toString());
      }

      const hasItemTo = url.searchParams.has(toKey, `${selectedItem.to}`);

      if (!hasItemTo && selectedItem.to) {
        url.searchParams.append(toKey, selectedItem.to.toString());
      }
    });

    // deleting items
    prevSelectedOptions.forEach((prevSelectedItem) => {
      const fromKey = `${filterName}[${prevSelectedItem.id}][from]`;
      const toKey = `${filterName}[${prevSelectedItem.id}][to]`;

      const hasItemFrom = url.searchParams.has(fromKey, `${prevSelectedItem.from}`);
      const shouldKeepFrom = selectedItems.find(
        (selectedItem) => `${filterName}[${selectedItem.id}][from]` === fromKey
      );

      if (hasItemFrom && !shouldKeepFrom) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        url.searchParams.delete(fromKey, prevSelectedItem.from);
      }

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const hasItemTo = url.searchParams.has(toKey, `${prevSelectedItem.to}`);
      const shouldKeepTo = selectedItems.find((selectedItem) => `${filterName}[${selectedItem.id}][to]` === toKey);

      if (hasItemTo && !shouldKeepTo) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        url.searchParams.delete(toKey, prevSelectedItem.to);
      }
    });

    paginationReset(url);
    history.push({ search: url.searchParams.toString() });
  };

  return {
    updateFilterKeyURLParamCommand,
    updateFromToURLParamCommand,
    updateURLParamCommand,
    paginationReset,
  };
};

export function getInfinityListPropsFromQuery<TResponse = Object>(
  params: UseInfiniteQueryResult<CollectionSuccessApiResponse<TResponse>> & {
    setSearchParamQuery: (value: string) => void;
    selectedOptions: Option[];
  }
) {
  return {
    loading: params.isLoading,
    isFetchingNextPage: params.isFetchingNextPage,
    isFetching: params.isFetching,
    hasNextPage: !!params.hasNextPage,
    fetchNextPage: params.fetchNextPage,
    totalItems: params.data?.pages[0]?.meta.total ?? 0,
    totalItemsWithoutFilters: params.data?.pages[0]?.meta.total_without_filters ?? 0,
    removeQuery: params.remove,
    onChangeSearchQuery: params.setSearchParamQuery,
    initialSelectedOptions: params.selectedOptions,
  };
}
